/*
  cart-price-heal.js

  Self-healing for $0 cart lines that originated from the slab configurator.
  Shopify's cart API can briefly report a line at $0 after a freshly created
  variant is added (while variant.price replicates across Shopify's caches).
  Customers don't pay $0 at checkout — but seeing $0 in the cart is alarming.

  Behavior:
    1. Watch the cart drawer / cart page for re-renders.
    2. When any line has `properties._price` AND final_price == 0, start
       polling /cart.js on a backoff schedule (0.75 → 7 s, ~14 s total).
    3. On recovery, fetch the cart with section HTML and swap in the
       refreshed cart drawer / cart page sections so the real price renders.
    4. If recovery never happens, remove the line and show a toast.

  Scoped to our lines by the `_price` property — native Shopify products
  don't have it, so they're never touched.

  Focal-theme notes: the script tolerates whatever cart custom elements are
  present (`<cart-drawer>`, `<cart-items>`, etc.) and discovers section ids
  from `<div id="shopify-section-...">` mounts on the page.
*/

(function () {
  'use strict';

  if (window.__slabCartHealLoaded) return;
  window.__slabCartHealLoaded = true;

  var POLL_BACKOFFS_MS = [750, 2000, 4000, 7000];
  var healing = {};
  var scanDebounce = null;
  // Focal uses <cart-drawer> + <cart-notification-drawer> + <cart-count>.
  // Dawn-era tags (cart-items, cart-notification) are kept so the script
  // stays portable; querySelector simply returns empty for absent tags.
  var CART_ELEMENT_TAGS = [
    'cart-drawer',
    'cart-notification-drawer',
    'cart-notification',
    'cart-items',
    'cart-count'
  ];

  // ── Section discovery ──────────────────────────────────────────────────
  function getCartSectionIds() {
    var ids = {};
    var els = document.querySelectorAll('[id^="shopify-section-"]');
    for (var i = 0; i < els.length; i++) {
      var id = els[i].id.replace('shopify-section-', '');
      // Only include sections that contain cart-related custom elements
      for (var t = 0; t < CART_ELEMENT_TAGS.length; t++) {
        if (els[i].querySelector(CART_ELEMENT_TAGS[t])) { ids[id] = true; break; }
      }
    }
    return Object.keys(ids);
  }

  // ── HTTP helpers ───────────────────────────────────────────────────────
  function fetchCartJson() {
    return fetch('/cart.js?_=' + Date.now(), {
      credentials: 'same-origin', cache: 'no-store',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }).then(function (r) { return r.json(); });
  }

  function fetchCartWithSections() {
    var ids = getCartSectionIds();
    var url = '/cart.js?_=' + Date.now();
    if (ids.length) url += '&sections=' + encodeURIComponent(ids.join(','));
    return fetch(url, {
      credentials: 'same-origin', cache: 'no-store',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }).then(function (r) { return r.json(); });
  }

  function applySections(sectionsMap) {
    if (!sectionsMap) return;
    Object.keys(sectionsMap).forEach(function (sectionId) {
      var html = sectionsMap[sectionId];
      if (!html) return;
      var mount = document.getElementById('shopify-section-' + sectionId);
      if (!mount) return;
      var newDoc = new DOMParser().parseFromString(html, 'text/html');
      var newRoot = newDoc.getElementById('shopify-section-' + sectionId);
      mount.innerHTML = newRoot ? newRoot.innerHTML : newDoc.body.innerHTML;
    });
  }

  // ── Identify our broken lines ─────────────────────────────────────────
  function findOurZeroLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(function (item) {
      var hasMarker = item.properties && typeof item.properties._price !== 'undefined' && item.properties._price !== '';
      var raw = (item.final_price != null) ? item.final_price : item.price;
      var cents = (typeof raw === 'number') ? raw : parseInt(raw, 10);
      return hasMarker && cents === 0;
    });
  }

  function removeLine(lineKey) {
    var ids = getCartSectionIds();
    var body = { id: lineKey, quantity: 0 };
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }
    return fetch('/cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); }).then(function (data) {
      if (data && data.sections) applySections(data.sections);
      return data;
    });
  }

  // ── Toast ──────────────────────────────────────────────────────────────
  function showToast(msg) {
    var existing = document.getElementById('cfg-cart-heal-toast');
    if (existing) existing.parentNode.removeChild(existing);
    var t = document.createElement('div');
    t.id = 'cfg-cart-heal-toast';
    t.setAttribute('role', 'alert');
    t.style.cssText =
      'position:fixed;top:20px;left:50%;transform:translateX(-50%);' +
      'background:#c0392b;color:#fff;padding:12px 18px;border-radius:4px;' +
      'z-index:99999;font-size:14px;line-height:1.4;max-width:90vw;' +
      'box-shadow:0 4px 12px rgba(0,0,0,0.18);';
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 6000);
  }

  // ── Polling state machine ──────────────────────────────────────────────
  function pollLine(lineKey, attempt) {
    attempt = attempt || 0;
    if (attempt >= POLL_BACKOFFS_MS.length) {
      console.warn('[cart-heal] line ' + lineKey + ' did not recover — removing');
      delete healing[lineKey];
      removeLine(lineKey).then(function () {
        showToast("Pricing wasn't ready for one of your items. Please add it again.");
      }).catch(function (err) { console.error('[cart-heal] remove failed:', err); });
      return;
    }
    setTimeout(function () {
      fetchCartWithSections().then(function (data) {
        var stillZero = findOurZeroLines(data).some(function (it) { return it.key === lineKey; });
        if (stillZero) return pollLine(lineKey, attempt + 1);
        console.log('[cart-heal] line ' + lineKey + ' recovered on attempt ' + (attempt + 1));
        delete healing[lineKey];
        if (data && data.sections) applySections(data.sections);
      }).catch(function (err) {
        console.warn('[cart-heal] poll error, retrying:', err);
        pollLine(lineKey, attempt + 1);
      });
    }, POLL_BACKOFFS_MS[attempt]);
  }

  function scanAndHeal() {
    fetchCartJson().then(function (cart) {
      findOurZeroLines(cart).forEach(function (line) {
        if (healing[line.key]) return;
        console.log('[cart-heal] $0 on configurator line ' + line.key + ' — starting heal');
        healing[line.key] = true;
        pollLine(line.key, 0);
      });
    }).catch(function () {});
  }

  function scheduleScan() {
    if (scanDebounce) clearTimeout(scanDebounce);
    scanDebounce = setTimeout(scanAndHeal, 250);
  }

  // ── Observer ───────────────────────────────────────────────────────────
  function initObserver() {
    var targets = [];
    CART_ELEMENT_TAGS.forEach(function (tag) {
      document.querySelectorAll(tag).forEach(function (el) { targets.push(el); });
    });
    if (!targets.length) return;
    var observer = new MutationObserver(scheduleScan);
    targets.forEach(function (t) { observer.observe(t, { childList: true, subtree: true }); });
  }

  function init() {
    initObserver();
    scheduleScan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
